/// <reference types="gtag.js" />

import type { ConsentSignals } from '@/lib/consent/consent-signals';

import type { AnalyticsAdapter, CaptureOptions } from './types';

import { redactCredentialBearingUrl } from '../credential-bearing-path';
import { publishConsentDefault } from './consent-mode-bootstrap';
import { getOrCreateGtag } from './data-layer';
import { stripPiiKeys } from './strip-pii';

/**
 * Whitelist of identify-trait fields permitted to flow to GA4.
 *
 * Google's GA4 Terms of Service prohibit sending PII (email, full name, etc.)
 * to GA4 properties. Violations can get the property terminated.
 *
 * Adding a new field here is a privacy-policy decision. New traits added to
 * the AnalyticsClient.identify payload will silently NOT flow to GA unless
 * they appear in this list — that is the safe default.
 */
const GA_USER_PROPERTY_ALLOWLIST = ['side', 'organizationId'] as const;
type AllowedGaUserPropertyKey = (typeof GA_USER_PROPERTY_ALLOWLIST)[number];

/**
 * Module-scoped guard so multiple adapter instances on the same page can't
 * inject the gtag.js script twice or re-fire the initial js/config calls.
 * The `getOrCreateAnalyticsClient` cache means we only construct
 * one adapter per page in practice — this is belt-and-braces against
 * future bypasses (tests, dev hot-reload, future call sites).
 */
let gtagBootstrapped = false;

function bootstrapGtag(measurementId: string): boolean {
  if (globalThis.document === undefined) return false;
  if (gtagBootstrapped) return true;
  gtagBootstrapped = true;

  // Consent Mode state before anything else, because GA4's built-in consent
  // checks read an unset value as granted rather than denied. Called here
  // rather than trusted to a sibling: this function is one of the two places
  // that puts a Google script on the page, and it is the one that decides
  // when gtag.js starts evaluating.
  publishConsentDefault();

  // Standard gtag.js bootstrap: the dataLayer queue and `gtag` are in place
  // BEFORE the remote script loads, so calls made now are buffered and
  // processed once gtag.js loads asynchronously. Both come from
  // `data-layer.ts`, shared with the consent bootstrap.
  const gtag = getOrCreateGtag();

  gtag('js', new Date());
  gtag('config', measurementId, {
    // Our router subscription owns page views; suppress the auto initial one
    // so we don't double-count on first load.
    send_page_view: false,
    // A credential-bearing path is redacted here, on the config, rather
    // than by declining to bootstrap: GA4's automatic events are built
    // inside gtag from the tag's own page context and never pass through
    // this adapter's methods, so a per-event override cannot reach them,
    // and `send_page_view: false` suppresses only the initial pageview.
    // `user_engagement` after a dwell is the case that matters — a
    // password-reset form is exactly the page someone sits on with a live
    // token in the path. Config-level values are inherited by those hits,
    // and a later config with `update: true` (which every pageview sends)
    // replaces them, so the redaction cannot outlive the token route.
    ...credentialSafePageContext(),
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
    measurementId,
  )}`;
  document.head.append(script);

  return true;
}

/**
 * Test-only reset hook — clears module-scoped bootstrap state AND the shared
 * globals (`gtag`, `dataLayer`), so each test starts from a clean slate
 * without callers having to remember to wipe them separately.
 */
export function __resetGoogleAnalyticsAdapterModuleStateForTesting() {
  gtagBootstrapped = false;
  Reflect.deleteProperty(globalThis, 'gtag');
  globalThis.dataLayer = undefined;
}

export class GoogleAnalyticsAdapter implements AnalyticsAdapter {
  readonly name = 'ga' as const;
  private initialized = false;
  private readonly measurementId: string;

  constructor(config: { measurementId: string }) {
    this.measurementId = config.measurementId;
    this.initialized = bootstrapGtag(this.measurementId);
  }

  /**
   * GA4 SPA pageview flow: first update the tag's current page context,
   * then emit the page_view event. Updating config is what lets subsequent
   * automatic events (engagement, scrolls, clicks) inherit the virtual page.
   */
  page(
    name: string,
    properties?: Record<string, unknown>,
    options?: CaptureOptions,
  ) {
    if (!this.isReady()) return;
    const { title, path, pageLocation, previousPageLocation, ...rest } =
      stripPiiKeys(properties) ?? {};

    const pageTitle = getString(title) ?? getDocumentTitle();
    const currentPageLocation =
      getString(pageLocation) ?? getCurrentPageLocation();
    // Redacted after the fallback, not before it: a caller that omits the
    // referrer gets `document.referrer`, which on the page after a token
    // route is the token URL.
    const pageReferrer = redactPageUrl(
      getString(previousPageLocation) ?? getDocumentReferrer(),
    );
    const pagePath =
      getString(path) ?? (name.startsWith('/') ? name : undefined);

    const config: Record<string, unknown> = {
      send_page_view: false,
      update: true,
    };
    if (pageTitle) config.page_title = pageTitle;
    if (currentPageLocation) config.page_location = currentPageLocation;
    if (pageReferrer) config.page_referrer = pageReferrer;
    globalThis.gtag?.('config', this.measurementId, config);

    const params: Record<string, unknown> = {
      ...rest,
      screen_name: name,
    };
    if (pagePath) params.page_path = pagePath;
    if (pageTitle) params.page_title = pageTitle;
    if (currentPageLocation) params.page_location = currentPageLocation;
    if (pageReferrer) params.page_referrer = pageReferrer;
    if (options?.immediate) params.transport_type = 'beacon';
    globalThis.gtag?.('event', 'page_view', params);
  }

  track(
    event: string,
    properties?: Record<string, unknown>,
    options?: CaptureOptions,
  ) {
    if (!this.isReady()) return;
    // No local enabled gate — gtag respects the Consent Mode state
    // published by `consent-mode-bootstrap.ts`.
    //
    // Strip well-known PII keys from event properties as defense-in-depth.
    // Call sites are responsible for not passing PII, but the blocklist
    // catches the common mistakes (matches `trackException`'s posture).
    const baseParams = stripPiiKeys(properties);
    // `options.immediate` (used for events that must survive page unload)
    // maps to GA4's `transport_type: 'beacon'`, which makes gtag use
    // `navigator.sendBeacon`.
    const extras: Record<string, unknown> = credentialSafePageContext();
    if (options?.immediate) extras.transport_type = 'beacon';
    // A propertyless event keeps sending no params at all rather than an
    // empty object.
    const params =
      Object.keys(extras).length > 0
        ? { ...baseParams, ...extras }
        : baseParams;
    globalThis.gtag?.('event', sanitizeGa4EventName(event), params);
  }

  trackException(
    error: unknown,
    additionalProperties?: Record<string, unknown>,
    options?: CaptureOptions,
  ) {
    if (!this.isReady()) return;
    const description = error instanceof Error ? error.message : String(error);
    // Canonical `description` and `fatal` come AFTER the spread so a caller
    // accidentally (or intentionally) passing those keys in
    // `additionalProperties` cannot overwrite the computed error message or
    // the fatal flag. PII stripping is defense-in-depth on top.
    const params: Record<string, unknown> = {
      ...stripPiiKeys(additionalProperties),
      ...credentialSafePageContext(),
      description,
      fatal: false,
    };
    if (options?.immediate) params.transport_type = 'beacon';
    globalThis.gtag?.('event', 'exception', params);
  }

  identify(userId: string, traits?: Record<string, unknown>) {
    if (!this.isReady()) return;

    // `gtag('set', { user_id })` rather than `gtag('config', id, { user_id })`
    // — per GA4 docs, `config` should be called once at init. Re-calling
    // `config` also auto-fires a `page_view` event (unless explicitly
    // suppressed), which would silently inflate engagement counts.
    globalThis.gtag?.('set', { user_id: userId });

    const userProperties = this.filterTraitsForGa(traits);
    if (Object.keys(userProperties).length > 0) {
      globalThis.gtag?.('set', 'user_properties', userProperties);
    }
  }

  reset() {
    if (!this.isReady()) return;
    // Same `gtag('set', ...)` pattern as identify — avoids the spurious
    // `page_view` that a config call would fire on every logout.
    globalThis.gtag?.('set', { user_id: null });
    // Clear every allowlisted user property so anonymous hits aren't
    // mis-attributed. Iterates the allowlist (rather than hardcoding) so
    // a new entry can't drift between `identify` and `reset`.
    const cleared: Record<string, unknown> = {};
    for (const key of GA_USER_PROPERTY_ALLOWLIST) {
      cleared[toSnakeCase(key)] = null;
    }
    globalThis.gtag?.('set', 'user_properties', cleared);
  }

  isReady(): boolean {
    return this.initialized;
  }

  /**
   * Writes no Consent Mode state. `consent-mode-bootstrap.ts` is its single
   * writer and publishes on the same `gtag` this adapter uses, so GA4 reads
   * the signals from there; a second writer could answer the same signal
   * differently, and which one a tag saw would come down to load order.
   *
   * There is nothing left for GA to do with the signals: it has no local
   * `enabled` flag, and gtag itself decides what to send under a given
   * consent state.
   */
  setConsent(_signals: ConsentSignals) {
    // Intentional no-op — see above.
  }

  optIn() {
    // Intentional no-op — Consent Mode's writer owns the analytics-only
    // shorthand too (`publishAnalyticsConsent`).
  }

  optOut() {
    // Intentional no-op — see optIn.
  }

  toggleSessionRecording(_toState: 'on' | 'off') {
    // GA4 has no session recording equivalent; intentional no-op.
  }

  /**
   * Returns only the GA4-allowlisted subset of identify traits. Keys are
   * snake_cased to match GA4 conventions for user_properties.
   */
  private filterTraitsForGa(
    traits: Record<string, unknown> | undefined,
  ): Record<string, unknown> {
    if (!traits) return {};
    const result: Record<string, unknown> = {};
    for (const key of GA_USER_PROPERTY_ALLOWLIST) {
      const value = traits[key];
      if (value === undefined || value === null) continue;
      result[toSnakeCase(key)] = value;
    }
    return result;
  }
}

/**
 * GA4 event name rules: must start with a letter, contain only
 * `[A-Za-z0-9_]`, max 40 characters. Names that don't match are silently
 * dropped by GA4's backend.
 */
function sanitizeGa4EventName(name: string): string {
  const cleaned = name
    .replaceAll(/[^a-zA-Z0-9_]/g, '_')
    .replace(/^[^a-zA-Z]+/, '');
  const result = cleaned.length === 0 ? 'event' : cleaned;
  return result.slice(0, 40);
}

function toSnakeCase(key: AllowedGaUserPropertyKey): string {
  // Single source of truth for GA4 property name mapping.
  switch (key) {
    case 'side':
      return 'side';
    case 'organizationId':
      return 'organization_id';
  }
}

function getString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/**
 * The `page_location` / `page_referrer` overrides an event needs so that
 * gtag does not report a token.
 *
 * gtag collects the current URL and the referrer from the browser for
 * every hit it sends, not from the parameters — so an event fired while a
 * token route is open reports the token as `page_location`, and one fired
 * after leaving that route reports it as `page_referrer`, however clean
 * the event's own properties are. An explicit parameter overrides the
 * collected value, so each is passed only when what gtag would collect
 * carries a token. Ordinary pages add nothing and keep gtag's own
 * collection.
 *
 * `page_view` builds both parameters unconditionally and does not use
 * this.
 */
function credentialSafePageContext(): Record<string, string> {
  const overrides: Record<string, string> = {};
  addRedactedUrl(overrides, 'page_location', getCurrentPageLocation());
  addRedactedUrl(overrides, 'page_referrer', getDocumentReferrer());
  return overrides;
}

function addRedactedUrl(
  target: Record<string, string>,
  key: 'page_location' | 'page_referrer',
  value: string | undefined,
) {
  if (value === undefined) return;
  const redacted = redactPageUrl(value);
  // Unchanged means the value carries no token, and gtag's own collected
  // value is the better one — it is the same URL, and overriding it would
  // pin the page context of every event to whatever this call read.
  if (redacted === undefined || redacted === value) return;
  target[key] = redacted;
}

/** {@link redactCredentialBearingUrl}, tolerating an absent value. */
function redactPageUrl(url: string | undefined): string | undefined {
  return url === undefined ? undefined : redactCredentialBearingUrl(url);
}

function getCurrentPageLocation(): string | undefined {
  if (globalThis.window === undefined) return undefined;
  return globalThis.window.location.href.split('#')[0];
}

function getDocumentReferrer(): string | undefined {
  if (globalThis.document === undefined) return undefined;
  return globalThis.document.referrer || undefined;
}

function getDocumentTitle(): string | undefined {
  if (globalThis.document === undefined) return undefined;
  return globalThis.document.title || undefined;
}

/**
 * Factory: returns a `GoogleAnalyticsAdapter` when env has the measurement
 * ID, else `null`. Lets the provider just collect the non-null factory
 * results without knowing GA's activation rule.
 */
export function createGoogleAnalyticsAdapter(env: {
  GOOGLE_ANALYTICS_MEASUREMENT_ID: string;
}): AnalyticsAdapter | null {
  if (!env.GOOGLE_ANALYTICS_MEASUREMENT_ID) return null;
  return new GoogleAnalyticsAdapter({
    measurementId: env.GOOGLE_ANALYTICS_MEASUREMENT_ID,
  });
}
